/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
  REPORT_CASH_FLOW_FORECAST,
  ReportService,
  whenFormInvalid,
} from '@gh/builder-shared';
import { AelEvents, reportField, reportIdField } from '@gh/builder-shared-data';
import {
  createDefaultGridModel,
  ROW_DIRTY_CLASS,
  textMessageDialogRoute,
} from '@gh/common-internal-shared';
import { ActionFlow, DateTimeTypes, getRouteInitData } from '@gh/common-shared';
import { DateFormat } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  NotificationService,
  TextMessageDialogConfig,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  buildEditableDateTimeColumn,
  buildEditableSelectColumn,
  buildOptionSetFromArray,
  GridDefinition,
  GridDefinitionFactory,
} from '@gh/core-ui-grid';
import { createLink, toBoolean } from '@gh/core-util';
import { filter$ } from '@gh/rx/operators';
import { CashFlowForecastDetailsBusinessService } from '../../resources/cash-flow-forecast-details-business.service';
import {
  CashFlowForecastDetails,
  CashFlowForecastJob,
  cashFlowForecastJobFieldState,
  getCashFlowForecastJobId,
  validateCashFlowForecastJobField,
} from '../../resources/cash-flow-forecast.model';
import { CashFlowForecastDetailsFormState } from './cash-flow-forecast-details-form-state.service';

@Component({
  standalone: false,
  selector: 'gh-cash-flow-forecast-details',
  templateUrl: 'cash-flow-forecast-details.component.html',
  providers: [CashFlowForecastDetailsFormState],
})
export class CashFlowForecastDetailsComponent extends AbstractFormComponent<CashFlowForecastDetails> implements OnInit {
  gridDefinition: GridDefinition;
  private _isProcessEnabled: boolean;

  constructor(private route: ActivatedRoute,
              private gridDefinitionFactory: GridDefinitionFactory,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private waitings: WaitingService,
              private translations: TranslationService,
              private reports: ReportService,
              public form: CashFlowForecastDetailsFormState,
              private business: CashFlowForecastDetailsBusinessService,
              private userContext: BuilderUserContext,
              private navigations: BuilderNavigationService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions) {
    super();
  }

  get completeFlow(): ActionFlow<FormFlowState<CashFlowForecastDetails>> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(whenFormInvalid, () => this.handleErrors()),
      execute: this.commonActions.execute(({ value }) => this.business.update(value)),
      finish: ({ value }) => {
        this.form.merge(value);
        this.showReport();
      },
    });
  }

  ngOnInit(): void {
    const { details } = getRouteInitData(this.route);

    this.form.build(details);

    this.gridDefinition = this.buildGridDefinition();
  }

  onReset(): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.reset());
  }

  goToAel(): void {
    this.navigations.goToAel({
      label: 'lbl_cash_flow_forecast',
      entityId: this.userContext.office!.id,
      entityType: 'OFFICE',
      eventTypeIds: [AelEvents.Office.EditedVirtualSchedule],
    });
  }

  get isProcessEnabled(): boolean {
    return this._isProcessEnabled;
  }

  setIsProcessEnabled(enabled: boolean): void {
    this._isProcessEnabled = enabled;
  }

  onUseJobSchedulesChange(useJobSchedules: boolean): void {
    if (!useJobSchedules) {
      this.setIsProcessEnabled(useJobSchedules);
    }
    this.form.setUseJobSchedules(useJobSchedules);
  }

  onUseVirtualSchedulesChange(useVirtualSchedules: boolean): void {
    this.setIsProcessEnabled(useVirtualSchedules);
    this.form.setUseVirtualSchedules(useVirtualSchedules);
  }

  onFocusedCellChange(): void {
    this.form.resetFocusedCell();
  }

  private handleErrors(): void {
    this.form.focusInvalidCell();
    this.notifications.error('msg_he_incorrect_data_in_tables');
  }

  private buildGridDefinition(): GridDefinition {
    const model = createDefaultGridModel(({
      getId: getCashFlowForecastJobId,
      validate: validateCashFlowForecastJobField,
      getCellDef: cashFlowForecastJobFieldState,
      command: ({ jobId }, field, value) => this.form.setJobField(jobId, field, value),
    }));

    const templateOptionSet = buildOptionSetFromArray(this.form.templates);

    return this.gridDefinitionFactory.buildManualWithModel({
      columns: [
        { field: 'jobNumber', headerName: this.translations.get('lbl_job'), width: 150 },
        { field: 'customerName', headerName: this.translations.get('lbl_client'), width: 250 },
        { field: 'siteAddress', headerName: this.translations.get('lbl_site'), width: 400 },
        { field: 'drawRuleName', headerName: this.translations.get('lbl_draw_rule'), width: 200 },
        {
          field: 'templateId',
          ...buildEditableSelectColumn(templateOptionSet, model.getCellAccessor('templateId'), {
            forceClearable: true,
          }),
          headerName: this.translations.get('lbl_virtual_schedule'),
          width: 150,
        },
        {
          field: 'startDate',
          ...buildEditableDateTimeColumn(DateTimeTypes.ShortDate, model.getCellAccessor('startDate'), {
            format: DateFormat.Date,
            forceClearable: true,
          }),
          headerName: this.translations.get('lbl_virtual_start_date'),
          width: 150,
        },
      ],
      options: {
        defaultColDef: {
          sortable: false,
        },
        rowClassRules: {
          [ROW_DIRTY_CLASS]: ({ data }: { data: CashFlowForecastJob }) => toBoolean(data.isDirty),
        },
      },
      model,
    });
  }

  private showReport(): void {
    this.reports.generateUrl(REPORT_CASH_FLOW_FORECAST, [
      reportField('UseJobSchedule', this.form.value.useJobSchedules),
      reportField('UseVirtualSchedule', this.form.value.useVirtualSchedules),
      ...this.form.value.jobs.map(({ jobId }) => reportIdField('JobId', jobId)),
    ]).pipe(this.waitings.operator())
      .subscribe(
        (url) => {
          this.dialogs.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
            title: this.translations.get('lbl_link_cash_flow_forecast'),
            html: [
              this.translations.get('lbl_cff_link_text'),
              '<br>',
              createLink(url),
            ].join(''),
            buttons: [{ value: 'ok', title: this.translations.get('lbl_ok'), type: 'primary' }],
          });
        },
        this.notifications.processAll);
  }
}
